import React, { useEffect } from 'react';
import { useFinanceStore, useAccountFilterStore, useAuthStore } from '../store';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Wallet, Eye, EyeOff, ChevronDown, Building2, Banknote, Info } from 'lucide-react';
import { formatCurrency } from '../lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

export const AccountFilterBar = () => {
  const { bankAccounts, fetchBankAccounts } = useFinanceStore();
  const { token } = useAuthStore();
  const { 
    selectedAccountIds, 
    allSelected, 
    toggleAccount, 
    selectAll, 
    selectNone,
    isAccountSelected 
  } = useAccountFilterStore();
  
  const [isOpen, setIsOpen] = React.useState(false);

  useEffect(() => {
    if (token && bankAccounts.length === 0) {
      fetchBankAccounts();
    }
  }, [token, fetchBankAccounts, bankAccounts.length]);

  // Calculate totals
  const selectedAccounts = allSelected 
    ? bankAccounts 
    : bankAccounts.filter(acc => selectedAccountIds.includes(acc.id));
  
  // Helper: get correct balance for account type
  // For cash accounts (Espécie/Caixa), use operational_balance (all transactions)
  // For bank accounts, use current_balance (reconciled only)
  const getAccountBalance = (acc) => {
    const isCashAccount = acc.account_type === 'cash' || 
      acc.bank_name?.toLowerCase().includes('espécie') || 
      acc.bank_name?.toLowerCase().includes('caixa');
    
    return isCashAccount 
      ? (parseFloat(acc.operational_balance) || parseFloat(acc.current_balance) || 0)
      : (parseFloat(acc.current_balance) || 0);
  };
  
  const totalBalance = selectedAccounts.reduce((sum, acc) => {
    return sum + getAccountBalance(acc);
  }, 0);

  const selectedCount = allSelected ? bankAccounts.length : selectedAccountIds.length;

  if (!token || bankAccounts.length === 0) return null;

  return (
    <div className="bg-gradient-to-r from-slate-50 to-blue-50 border-b sticky top-0 z-30">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="px-2 md:px-4 py-1 md:py-2">
          {/* Summary Bar - Always visible */}
          <CollapsibleTrigger asChild>
            <div className="flex items-center justify-between cursor-pointer hover:bg-white/50 rounded-lg px-2 md:px-3 py-1.5 md:py-2 transition-colors">
              <div className="flex items-center gap-2 md:gap-4 min-w-0">
                <div className="flex items-center gap-1 md:gap-2 shrink-0">
                  <Building2 className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
                  <span className="font-medium text-xs md:text-sm hidden sm:inline">Contas Bancárias:</span>
                  <Badge variant="outline" className="bg-white text-[10px] md:text-xs">
                    {selectedCount}/{bankAccounts.length}
                  </Badge>
                </div>
                
                {/* Individual balances preview - hidden on small screens */}
                <div className="hidden lg:flex items-center gap-3">
                  {selectedAccounts.slice(0, 3).map(acc => {
                    const balance = getAccountBalance(acc);
                    return (
                      <div key={acc.id} className="flex items-center gap-1 text-xs bg-white px-2 py-1 rounded border">
                        <span className="text-muted-foreground">{acc.bank_name}:</span>
                        <span className={`font-medium ${balance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(balance)}
                        </span>
                      </div>
                    );
                  })}
                  {selectedAccounts.length > 3 && (
                    <span className="text-xs text-muted-foreground">+{selectedAccounts.length - 3} mais</span>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2 md:gap-4">
                <ChevronDown className={`h-4 w-4 md:h-5 md:w-5 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </div>
            </div>
          </CollapsibleTrigger>

          {/* Expanded Account Selection */}
          <CollapsibleContent>
            <div className="pt-2 md:pt-3 pb-2 border-t mt-2">
              {/* Quick actions */}
              <div className="flex items-center gap-2 mb-2 md:mb-3">
                <Button 
                  variant={allSelected ? "default" : "outline"} 
                  size="sm" 
                  onClick={selectAll}
                  className="h-6 md:h-7 text-[10px] md:text-xs"
                >
                  <Eye className="h-3 w-3 mr-1" />
                  Todas
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={selectNone}
                  className="h-6 md:h-7 text-[10px] md:text-xs"
                >
                  <EyeOff className="h-3 w-3 mr-1" />
                  Nenhuma
                </Button>
                <span className="text-[10px] md:text-xs text-muted-foreground ml-2 hidden sm:inline">
                  Clique nas contas para mostrar/ocultar nos relatórios
                </span>
              </div>

              {/* Account cards */}
              <TooltipProvider>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                  {bankAccounts.map(account => {
                    const isSelected = isAccountSelected(account.id);
                    const balance = getAccountBalance(account);
                    const isThirdParty = account.is_third_party;
                    const isCashAccount = account.account_type === 'cash' || 
                      account.bank_name?.toLowerCase().includes('espécie') || 
                      account.bank_name?.toLowerCase().includes('caixa');
                    
                    return (
                      <Card 
                        key={account.id}
                        className={`p-2 md:p-3 cursor-pointer transition-all hover:shadow-md ${
                          isSelected 
                            ? 'bg-white border-blue-300 ring-1 ring-blue-200' 
                            : 'bg-gray-100 border-gray-200 opacity-60'
                        } ${isThirdParty ? 'border-dashed' : ''} ${isCashAccount ? 'border-l-4 border-l-green-500' : ''}`}
                        onClick={() => toggleAccount(account.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Checkbox 
                                checked={isSelected}
                                className="pointer-events-none"
                              />
                              {isCashAccount ? (
                                <Banknote className="h-4 w-4 text-green-600 shrink-0" />
                              ) : (
                                <Building2 className="h-4 w-4 text-slate-500 shrink-0" />
                              )}
                              <span className="font-medium text-sm truncate">
                                {account.bank_name}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground truncate mt-1">
                              {account.name || account.account_number}
                            </p>
                            <div className="flex items-center gap-1 mt-1 flex-wrap">
                              {isThirdParty && (
                                <Badge variant="outline" className="text-[10px] bg-orange-50 text-orange-700 border-orange-200">
                                  Terceiro
                                </Badge>
                              )}
                              {isCashAccount && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200 cursor-help flex items-center gap-1">
                                      <Banknote className="h-3 w-3" />
                                      Caixa
                                      <Info className="h-3 w-3 ml-0.5" />
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent side="bottom" className="max-w-[250px] text-xs">
                                    <p className="font-medium mb-1">Conta tipo Caixa/Espécie</p>
                                    <p>O saldo desta conta considera TODAS as transações, não apenas as conciliadas.</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`text-sm font-bold ${balance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {formatCurrency(balance)}
                            </p>
                            {isCashAccount && account.current_balance !== account.operational_balance && (
                              <p className="text-[10px] text-muted-foreground">
                                (concil: {formatCurrency(account.current_balance || 0)})
                              </p>
                            )}
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </TooltipProvider>

              {/* Summary footer */}
              <div className="flex flex-col gap-2 mt-2 md:mt-3 pt-2 md:pt-3 border-t">
                {/* Legend - hidden on mobile */}
                <div className="hidden md:flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="font-medium">Legenda:</span>
                  <span className="flex items-center gap-1">
                    <Building2 className="h-3 w-3" /> Conta Bancária (saldo = conciliado)
                  </span>
                  <span className="flex items-center gap-1 text-green-700">
                    <Banknote className="h-3 w-3" /> Caixa/Espécie (saldo = todas transações)
                  </span>
                  <span className="flex items-center gap-1 text-orange-700">
                    <span className="border border-dashed border-orange-300 w-3 h-3 rounded-sm" /> Terceiro
                  </span>
                </div>
                
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                  <div className="text-[10px] md:text-sm text-muted-foreground">
                    {selectedCount === 0 
                      ? 'Nenhuma conta selecionada'
                      : `${selectedCount} conta(s) selecionada(s)`
                    }
                  </div>
                  <div className="flex items-center gap-2 md:gap-4 flex-wrap">
                    {selectedAccounts.map(acc => {
                      const balance = getAccountBalance(acc);
                      const isCash = acc.account_type === 'cash' || 
                        acc.bank_name?.toLowerCase().includes('espécie') || 
                        acc.bank_name?.toLowerCase().includes('caixa');
                      return (
                        <div key={acc.id} className="text-[10px] md:text-xs flex items-center gap-1">
                          {isCash ? <Banknote className="h-3 w-3 text-green-600" /> : <Building2 className="h-3 w-3 text-slate-400" />}
                          <span className="text-muted-foreground hidden sm:inline">{acc.bank_name}: </span>
                          <span className={`font-medium ${balance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {formatCurrency(balance)}
                          </span>
                        </div>
                      );
                    })}
                    <div className="text-xs md:text-sm font-bold border-l pl-2 md:pl-4">
                      Total: <span className={totalBalance < 0 ? 'text-red-600' : 'text-green-600'}>
                        {formatCurrency(totalBalance)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    </div>
  );
};

export default AccountFilterBar;
